"use client";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Icons } from "@/components/Icons";
import { Badge, Button, Card, TopBar } from "@/components/ui";
import { SignInModal } from "@/components/SignInModal";
import { useLoyalty, useLoyaltyTransactions, type LoyaltyTransaction } from "@/hooks/useLoyalty";
import { useAuth } from "@/store/auth";
import { useFormatPrice } from "@/hooks/useFormatPrice";
import { Price } from "@/components/Price";

function transactionTone(tx: LoyaltyTransaction): "success" | "warn" | "danger" | "neutral" {
  if (tx.pointsChange > 0 || tx.cashbackChange > 0) return "success";
  if (tx.pointsChange < 0 || tx.cashbackChange < 0) return "danger";
  if (tx.status?.toLowerCase() === "pending") return "warn";
  return "neutral";
}

function signPrefix(n: number): string {
  if (n > 0) return "+";
  return "";
}

export function PointsHistoryScreen() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { format } = useFormatPrice();
  const { isLoggedIn } = useAuth();
  const { balance, loading: balanceLoading } = useLoyalty();
  const { transactions, loading, error, refresh } = useLoyaltyTransactions();
  const [signInOpen, setSignInOpen] = useState(false);

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }),
    [i18n.language],
  );

  const totalEarned = useMemo(
    () =>
      transactions.reduce(
        (sum, tx) => sum + (tx.pointsChange > 0 ? tx.pointsChange : 0),
        0,
      ),
    [transactions],
  );

  if (!isLoggedIn) {
    return (
      <div style={{ paddingBottom: 80 }}>
        <TopBar title={t("v2.points_history.title")} onBack={() => navigate("/loyalty-wallet")} />
        <div
          style={{
            padding: "42px 24px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 14,
          }}
        >
          <div
            style={{
              width: 76,
              height: 76,
              borderRadius: 999,
              background: "var(--primary-50)",
              display: "grid",
              placeItems: "center",
              color: "var(--primary-700)",
            }}
          >
            <Icons.receipt size={32} />
          </div>
          <div className="display" style={{ fontSize: 18, fontWeight: 700, color: "var(--ink-1)" }}>
            {t("v2.points_history.signin_title")}
          </div>
          <div style={{ maxWidth: 280, fontSize: 13, lineHeight: 1.45, color: "var(--ink-2)" }}>
            {t("v2.points_history.signin_desc")}
          </div>
          <Button size="lg" onClick={() => setSignInOpen(true)}>
            {t("v2.profile.signin_phone")}
          </Button>
        </div>
        <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: 80 }}>
      <TopBar
        title={t("v2.points_history.title")}
        onBack={() => navigate("/loyalty-wallet")}
        right={
          <Button variant="ghost" size="sm" onClick={() => refresh()} disabled={loading}>
            {t("v2.common.retry")}
          </Button>
        }
      />

      <div style={{ padding: "12px 16px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
        {balance?.programType !== "cashback" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Card padding={14}>
              <div style={{ fontSize: 12, color: "var(--ink-3)", fontWeight: 700 }}>{t("v2.points_history.current_points")}</div>
              <div className="display" style={{ marginTop: 6, fontSize: 28, fontWeight: 800, color: "var(--ink-1)" }}>
                {balanceLoading && !balance ? "…" : balance?.points ?? 0}
              </div>
              <div style={{ fontSize: 12, color: "var(--ink-3)" }}>{t("v2.profile.points")}</div>
            </Card>

            <Card padding={14}>
              <div style={{ fontSize: 12, color: "var(--ink-3)", fontWeight: 700 }}>{t("v2.points_history.total_earned")}</div>
              <div className="display" style={{ marginTop: 6, fontSize: 28, fontWeight: 800, color: "var(--ink-1)" }}>{totalEarned}</div>
              <div style={{ fontSize: 12, color: "var(--ink-3)" }}>{t("v2.points_history.points_all_time")}</div>
            </Card>
          </div>
        )}

        {balance?.cashback ? (
          <Card padding={14} style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 12,
                background: "var(--primary-50)",
                color: "var(--primary-700)",
                display: "grid",
                placeItems: "center",
              }}
            >
              <Icons.ticket size={17} />
            </div>
            <div style={{ flex: 1 }}>
              <div className="display" style={{ fontSize: 14, fontWeight: 700, color: "var(--ink-1)" }}>
                {t("v2.points_history.cashback_available")}
              </div>
              <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
                {t("v2.points_history.cashback_desc")}
              </div>
            </div>
            <Badge tone="primary"><Price amount={balance.cashback} /></Badge>
          </Card>
        ) : null}

        {error && (
          <Card padding={14} style={{ borderColor: "#fecaca", background: "#fff7f7" }}>
            <div style={{ fontSize: 13, color: "#991b1b", fontWeight: 700 }}>
              {t("v2.points_history.couldnt_load")}
            </div>
            <div style={{ marginTop: 4, fontSize: 12, color: "#7f1d1d" }}>{error.message}</div>
          </Card>
        )}

        {loading && transactions.length === 0 ? (
          <Card padding={20} style={{ textAlign: "center", color: "var(--ink-3)", fontSize: 13 }}>
            {t("v2.points_history.loading")}
          </Card>
        ) : transactions.length === 0 ? (
          <Card padding={22} style={{ textAlign: "center" }}>
            <div style={{ margin: "0 auto 12px", width: 54, height: 54, borderRadius: 999, background: "var(--surface-2)", display: "grid", placeItems: "center", color: "var(--ink-3)" }}>
              <Icons.receipt size={24} />
            </div>
            <div className="display" style={{ fontSize: 15, fontWeight: 700, color: "var(--ink-1)" }}>
              {t("v2.points_history.empty_title")}
            </div>
            <div style={{ marginTop: 5, fontSize: 13, color: "var(--ink-2)", lineHeight: 1.45 }}>
              {t("v2.points_history.empty_desc")}
            </div>
          </Card>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {transactions.map((tx) => {
              const tone = transactionTone(tx);
              const date = tx.createdAt ? dateFmt.format(new Date(tx.createdAt)) : "";
              const hasCashback = tx.cashbackChange !== 0;
              return (
                <Card key={tx.id} padding={14} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 12,
                      background:
                        tone === "success"
                          ? "#e7f7ee"
                          : tone === "danger"
                            ? "#fee2e2"
                            : "var(--surface-2)",
                      color:
                        tone === "success"
                          ? "#08793e"
                          : tone === "danger"
                            ? "#b91c1c"
                            : "var(--ink-2)",
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    {tone === "success" ? <Icons.plus size={17} /> : tone === "danger" ? <Icons.minus size={17} /> : <Icons.receipt size={17} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                      <div className="display" style={{ fontSize: 14, fontWeight: 700, color: "var(--ink-1)" }}>
                        {tx.description}
                      </div>
                      {tx.status && <Badge tone={tone}>{tx.status}</Badge>}
                    </div>
                    <div style={{ marginTop: 3, fontSize: 12, color: "var(--ink-3)" }}>
                      {date}
                      {tx.orderNumber ? ` · ${t("v2.points_history.order_number", { n: tx.orderNumber })}` : ""}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    {balance?.programType === "cashback" ? (
                      <>
                        <div className="display"
                          style={{
                            fontSize: 14,
                            fontWeight: 800,
                            color: tx.cashbackChange >= 0 ? "#08793e" : "#b91c1c",
                          }}
                        >
                          {signPrefix(tx.cashbackChange)}
                          <Price amount={tx.cashbackChange} />
                        </div>
                        {tx.pointsChange !== 0 && (
                          <div style={{ fontSize: 11, color: "var(--ink-3)" }}>
                            {signPrefix(tx.pointsChange)}{tx.pointsChange} {t("v2.profile.pts")}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="display"
                          style={{
                            fontSize: 14,
                            fontWeight: 800,
                            color: tx.pointsChange >= 0 ? "#08793e" : "#b91c1c",
                          }}
                        >
                          {signPrefix(tx.pointsChange)}
                          {tx.pointsChange}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--ink-3)" }}>
                          {hasCashback ? <Price amount={tx.cashbackChange} /> : t("v2.profile.pts")}
                        </div>
                      </>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
